import { t } from "@lingui/macro";
import { useMemo } from "react";
import { Link, matchRoutes, Outlet, useLocation, useSearchParams } from "react-router";

import { LocaleSwitch } from "@/client/components/locale-switch";
import { Logo } from "@/client/components/logo";
import { ThemeSwitch } from "@/client/components/theme-switch";
import { useAuthProviders } from "@/client/services/auth/providers";

import { SocialAuth } from "./_components/social-auth";
import "./layout.css";

const authRoutes = [{ path: "/auth/login" }, { path: "/auth/register" }];

export const AuthLayout = () => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { providers } = useAuthProviders();
  const isAuthRoute = useMemo(() => matchRoutes(authRoutes, location) !== null, [location]);

  // Get the provider from query parameters or localStorage as fallback
  const lastUsedProvider = useMemo(() => {
    const providerFromUrl = searchParams.get("provider");
    if (providerFromUrl) return providerFromUrl;

    // Fallback to localStorage
    try {
      return localStorage.getItem("lastUsedProvider");
    } catch {
      return null;
    }
  }, [searchParams]);

  if (!providers) return null;

  // Condition (providers.length === 1) hides the divider if providers[] includes only "email"
  const hideDivider = !providers.includes("email") || providers.length === 1;

  return (
    <div className="flex min-h-screen">
      <div className="basis-full lg:basis-1/2">
        <div className="flex h-full flex-col justify-between p-6 lg:p-8">
          <div className="flex items-center justify-between">
            <Link
              to="/"
              className="flex items-center space-x-3 transition-opacity hover:opacity-80"
            >
              <Logo size={48} />
            </Link>

            <div className="flex items-center space-x-2">
              <LocaleSwitch />
              <ThemeSwitch />
            </div>
          </div>

          <div className="mx-auto w-full max-w-sm space-y-8">
            <Outlet />

            {isAuthRoute && (
              <>
                {!hideDivider && (
                  <div className="flex items-center space-x-4 text-xs font-medium">
                    <hr className="flex-1" />
                    <span className="text-muted-foreground">{t`or continue with`}</span>
                    <hr className="flex-1" />
                  </div>
                )}

                <SocialAuth lastUsedProvider={lastUsedProvider || undefined} />
              </>
            )}
          </div>

          <div />
        </div>
      </div>

      {/* Right Side */}
      <div className="hidden basis-1/2 bg-zinc-50 lg:flex dark:bg-zinc-900">
        <div className="relative flex w-full flex-col justify-center overflow-hidden p-8">
          {/* Animated Background Pattern */}
          <div className="absolute inset-0">
            {/* Animated Gradient Overlay */}
            <div className="absolute inset-0 opacity-20">
              <div
                className="absolute inset-0 bg-gradient-to-br from-zinc-100/10 via-zinc-300/10 to-zinc-100/10 dark:from-zinc-800/10 dark:via-zinc-600/10 dark:to-zinc-800/10"
                style={{
                  background:
                    "linear-gradient(-45deg, rgba(0, 0, 0, 0.05), rgba(64, 64, 64, 0.1), rgba(128, 128, 128, 0.05), rgba(0, 0, 0, 0.05))",
                  backgroundSize: "400% 400%",
                  animation: "gradientShift 15s ease infinite",
                }}
              />
            </div>

            {/* Main Animated Circles */}
            <div className="absolute inset-0 opacity-[0.03] dark:opacity-[0.08]">
              <div
                className="absolute top-1/4 left-1/4 h-64 w-64 rounded-full bg-zinc-900 blur-3xl dark:bg-zinc-100"
                style={{
                  animation: "float 20s ease-in-out infinite, pulse 8s ease-in-out infinite",
                }}
              />
              <div
                className="absolute right-1/4 bottom-1/4 h-80 w-80 rounded-full bg-zinc-800 blur-3xl dark:bg-zinc-200"
                style={{
                  animation:
                    "float 25s ease-in-out infinite reverse, pulse 10s ease-in-out infinite 2s",
                }}
              />
              <div
                className="absolute top-1/2 right-1/3 h-48 w-48 rounded-full bg-zinc-700 blur-2xl dark:bg-zinc-300"
                style={{
                  animation: "float 18s ease-in-out infinite 5s, pulse 6s ease-in-out infinite 1s",
                }}
              />
            </div>

            {/* Floating Particles */}
            <div className="absolute inset-0 opacity-15">
              {/* Small floating dots */}
              {[...Array(12)].map((_, i) => (
                <div
                  key={i}
                  className="absolute rounded-full bg-zinc-600 dark:bg-zinc-400"
                  style={{
                    width: Math.random() * 4 + 2 + "px",
                    height: Math.random() * 4 + 2 + "px",
                    left: Math.random() * 100 + "%",
                    top: Math.random() * 100 + "%",
                    animation: `floatParticle ${15 + Math.random() * 10}s linear infinite ${Math.random() * 5}s`,
                  }}
                />
              ))}
            </div>

            {/* Animated Lines */}
            <div className="absolute inset-0 opacity-3">
              <svg className="absolute inset-0 h-full w-full" xmlns="http://www.w3.org/2000/svg">
                <defs>
                  <linearGradient id="lineGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="currentColor" stopOpacity="0" />
                    <stop offset="50%" stopColor="currentColor" stopOpacity="0.2" />
                    <stop offset="100%" stopColor="currentColor" stopOpacity="0" />
                  </linearGradient>
                </defs>
                <path
                  d="M0,100 Q250,50 500,100 T1000,100"
                  stroke="url(#lineGrad)"
                  strokeWidth="2"
                  fill="none"
                  className="text-zinc-700 dark:text-zinc-300"
                  style={{
                    strokeDasharray: "10,5",
                    animation: "drawLine 20s ease-in-out infinite",
                  }}
                />
                <path
                  d="M0,200 Q300,150 600,200 T1200,200"
                  stroke="url(#lineGrad)"
                  strokeWidth="1.5"
                  fill="none"
                  className="text-zinc-700 dark:text-zinc-300"
                  style={{
                    strokeDasharray: "8,4",
                    animation: "drawLine 25s ease-in-out infinite 5s",
                  }}
                />
              </svg>
            </div>

            {/* Resume & Job Themed Elements */}
            <div className="absolute inset-0 opacity-10">
              {/* Floating Resume Icons */}
              <div
                className="absolute top-1/4 left-1/5 text-zinc-600 dark:text-zinc-400"
                style={{
                  animation: "float 30s ease-in-out infinite, rotate 45s linear infinite",
                }}
              >
                <svg className="h-8 w-8" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6z" />
                  <path d="M14 2v6h6M16 13H8M16 17H8M10 9H8" />
                </svg>
              </div>

              {/* AI Brain Icon */}
              <div
                className="absolute top-3/5 right-1/4 text-zinc-500 dark:text-zinc-500"
                style={{
                  animation:
                    "pulse 8s ease-in-out infinite 2s, float 35s ease-in-out infinite reverse",
                }}
              >
                <svg className="h-10 w-10" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.94-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z" />
                </svg>
              </div>

              {/* Briefcase Icon */}
              <div
                className="absolute bottom-1/3 left-2/3 text-zinc-600 dark:text-zinc-400"
                style={{
                  animation:
                    "scaleBreath 12s ease-in-out infinite 4s, rotate 60s linear infinite reverse",
                }}
              >
                <svg className="h-7 w-7" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M20 6h-2.18c.11-.31.18-.65.18-1a2.996 2.996 0 0 0-5.5-1.65l-.5.67-.5-.68C10.96 2.54 10.05 2 9 2 7.34 2 6 3.34 6 5c0 .35.07.69.18 1H4c-1.11 0-1.99.89-1.99 2L2 19c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V8c0-1.11-.89-2-2-2zm-5-2.84c.76 0 1.38.62 1.38 1.38s-.62 1.38-1.38 1.38-1.38-.62-1.38-1.38.62-1.38 1.38-1.38zM9 4.62c.76 0 1.38.62 1.38 1.38S9.76 7.38 9 7.38 7.62 6.76 7.62 6s.62-1.38 1.38-1.38zM20 19H4V8h16v11z" />
                </svg>
              </div>

              {/* Chart/Analytics Icon */}
              <div
                className="absolute top-1/6 right-1/6 text-zinc-500 dark:text-zinc-500"
                style={{
                  animation: "float 25s ease-in-out infinite 3s, pulse 10s ease-in-out infinite",
                }}
              >
                <svg className="h-9 w-9" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M3.5 18.49l6-6.01 4 4L22 6.92l-1.41-1.41-7.09 7.97-4-4L2 16.99z" />
                </svg>
              </div>
            </div>

            {/* AI Neural Network Pattern */}
            <div className="absolute inset-0 opacity-5">
              <svg className="absolute inset-0 h-full w-full" xmlns="http://www.w3.org/2000/svg">
                <defs>
                  <radialGradient id="nodeGrad" cx="50%" cy="50%" r="50%">
                    <stop offset="0%" stopColor="currentColor" stopOpacity="0.8" />
                    <stop offset="100%" stopColor="currentColor" stopOpacity="0" />
                  </radialGradient>
                </defs>

                {/* Neural Network Nodes */}
                <circle
                  cx="15%"
                  cy="20%"
                  r="3"
                  fill="url(#nodeGrad)"
                  className="text-zinc-600 dark:text-zinc-400"
                >
                  <animate attributeName="r" values="2;4;2" dur="6s" repeatCount="indefinite" />
                </circle>
                <circle
                  cx="25%"
                  cy="40%"
                  r="2"
                  fill="url(#nodeGrad)"
                  className="text-zinc-600 dark:text-zinc-400"
                >
                  <animate
                    attributeName="r"
                    values="1;3;1"
                    dur="8s"
                    repeatCount="indefinite"
                    begin="2s"
                  />
                </circle>
                <circle
                  cx="35%"
                  cy="60%"
                  r="3"
                  fill="url(#nodeGrad)"
                  className="text-zinc-600 dark:text-zinc-400"
                >
                  <animate
                    attributeName="r"
                    values="2;4;2"
                    dur="7s"
                    repeatCount="indefinite"
                    begin="4s"
                  />
                </circle>
                <circle
                  cx="70%"
                  cy="25%"
                  r="2"
                  fill="url(#nodeGrad)"
                  className="text-zinc-600 dark:text-zinc-400"
                >
                  <animate
                    attributeName="r"
                    values="1;3;1"
                    dur="9s"
                    repeatCount="indefinite"
                    begin="1s"
                  />
                </circle>
                <circle
                  cx="85%"
                  cy="45%"
                  r="3"
                  fill="url(#nodeGrad)"
                  className="text-zinc-600 dark:text-zinc-400"
                >
                  <animate
                    attributeName="r"
                    values="2;4;2"
                    dur="6s"
                    repeatCount="indefinite"
                    begin="3s"
                  />
                </circle>

                {/* Connection Lines */}
                <path
                  d="M15% 20% L25% 40%"
                  stroke="currentColor"
                  strokeWidth="0.5"
                  className="text-zinc-500 dark:text-zinc-500"
                  opacity="0.3"
                >
                  <animate
                    attributeName="opacity"
                    values="0.1;0.6;0.1"
                    dur="4s"
                    repeatCount="indefinite"
                  />
                </path>
                <path
                  d="M25% 40% L35% 60%"
                  stroke="currentColor"
                  strokeWidth="0.5"
                  className="text-zinc-500 dark:text-zinc-500"
                  opacity="0.3"
                >
                  <animate
                    attributeName="opacity"
                    values="0.1;0.6;0.1"
                    dur="5s"
                    repeatCount="indefinite"
                    begin="1s"
                  />
                </path>
                <path
                  d="M15% 20% L70% 25%"
                  stroke="currentColor"
                  strokeWidth="0.5"
                  className="text-zinc-500 dark:text-zinc-500"
                  opacity="0.3"
                >
                  <animate
                    attributeName="opacity"
                    values="0.1;0.6;0.1"
                    dur="6s"
                    repeatCount="indefinite"
                    begin="2s"
                  />
                </path>
                <path
                  d="M70% 25% L85% 45%"
                  stroke="currentColor"
                  strokeWidth="0.5"
                  className="text-zinc-500 dark:text-zinc-500"
                  opacity="0.3"
                >
                  <animate
                    attributeName="opacity"
                    values="0.1;0.6;0.1"
                    dur="4s"
                    repeatCount="indefinite"
                    begin="3s"
                  />
                </path>
              </svg>
            </div>

            {/* Space Stars Effect */}
            <div className="absolute inset-0 opacity-15">
              {/* Twinkling Stars */}
              {[...Array(20)].map((_, i) => (
                <div
                  key={`star-${i}`}
                  className="absolute rounded-full bg-zinc-500 dark:bg-zinc-300"
                  style={{
                    width: Math.random() * 2 + 1 + "px",
                    height: Math.random() * 2 + 1 + "px",
                    left: Math.random() * 100 + "%",
                    top: Math.random() * 100 + "%",
                    animation: `twinkle ${2 + Math.random() * 3}s ease-in-out infinite ${Math.random() * 2}s`,
                  }}
                />
              ))}
            </div>

            {/* Skill Badges Floating */}
            <div className="absolute inset-0 opacity-8">
              {/* JavaScript Badge */}
              <div
                className="absolute top-1/2 left-1/6 rounded bg-zinc-400/20 px-2 py-1 text-xs font-medium text-zinc-700 dark:bg-zinc-600/20 dark:text-zinc-300"
                style={{
                  animation:
                    "float 40s ease-in-out infinite 2s, scaleBreath 8s ease-in-out infinite",
                }}
              >
                JS
              </div>

              {/* AI Badge */}
              <div
                className="absolute right-1/3 bottom-1/4 rounded bg-zinc-500/20 px-2 py-1 text-xs font-medium text-zinc-600 dark:bg-zinc-500/20 dark:text-zinc-400"
                style={{
                  animation:
                    "float 35s ease-in-out infinite reverse 4s, pulse 12s ease-in-out infinite",
                }}
              >
                AI
              </div>

              {/* React Badge */}
              <div
                className="absolute top-2/3 right-1/5 rounded bg-zinc-600/15 px-2 py-1 text-xs font-medium text-zinc-700 dark:bg-zinc-400/15 dark:text-zinc-300"
                style={{
                  animation: "float 42s ease-in-out infinite 6s, rotate 80s linear infinite",
                }}
              >
                React
              </div>

              {/* Python Badge */}
              <div
                className="absolute top-1/3 left-3/4 rounded bg-zinc-500/15 px-2 py-1 text-xs font-medium text-zinc-600 dark:bg-zinc-500/15 dark:text-zinc-400"
                style={{
                  animation: "float 38s ease-in-out infinite 1s, pulse 9s ease-in-out infinite 3s",
                }}
              >
                Python
              </div>

              {/* Node.js Badge */}
              <div
                className="absolute bottom-1/6 left-1/2 rounded bg-zinc-400/15 px-2 py-1 text-xs font-medium text-zinc-700 dark:bg-zinc-600/15 dark:text-zinc-300"
                style={{
                  animation:
                    "scaleBreath 11s ease-in-out infinite 5s, float 45s ease-in-out infinite reverse",
                }}
              >
                Node
              </div>

              {/* TypeScript Badge */}
              <div
                className="absolute top-1/8 left-2/5 rounded bg-zinc-600/20 px-2 py-1 text-xs font-medium text-zinc-700 dark:bg-zinc-400/20 dark:text-zinc-300"
                style={{
                  animation: "rotate 70s linear infinite, float 33s ease-in-out infinite 7s",
                }}
              >
                TS
              </div>

              {/* AWS Badge */}
              <div
                className="absolute right-1/6 bottom-2/5 rounded bg-zinc-500/20 px-2 py-1 text-xs font-medium text-zinc-600 dark:bg-zinc-500/20 dark:text-zinc-400"
                style={{
                  animation:
                    "float 50s ease-in-out infinite 3s, scaleBreath 14s ease-in-out infinite",
                }}
              >
                AWS
              </div>
            </div>

            {/* More Career & Tech Icons */}
            <div className="absolute inset-0 opacity-8">
              {/* Trophy/Achievement Icon */}
              <div
                className="absolute top-1/5 right-2/5 text-zinc-600 dark:text-zinc-400"
                style={{
                  animation: "pulse 9s ease-in-out infinite 1s, rotate 55s linear infinite",
                }}
              >
                <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
                </svg>
              </div>

              {/* Settings/Gear Icon */}
              <div
                className="absolute bottom-1/5 left-1/3 text-zinc-500 dark:text-zinc-500"
                style={{
                  animation: "rotate 30s linear infinite, scaleBreath 7s ease-in-out infinite 4s",
                }}
              >
                <svg className="h-7 w-7" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 15.5A3.5 3.5 0 0 1 8.5 12A3.5 3.5 0 0 1 12 8.5a3.5 3.5 0 0 1 3.5 3.5 3.5 3.5 0 0 1-3.5 3.5m7.43-2.53c.04-.32.07-.64.07-.97 0-.33-.03-.66-.07-1l2.11-1.63c.19-.15.24-.42.12-.64l-2-3.46c-.12-.22-.39-.31-.61-.22l-2.49 1c-.52-.39-1.06-.73-1.69-.98l-.37-2.65A.506.506 0 0 0 14 2h-4c-.25 0-.46.18-.5.42l-.37 2.65c-.63.25-1.17.59-1.69.98l-2.49-1c-.22-.09-.49 0-.61.22l-2 3.46c-.13.22-.07.49.12.64L4.57 11c-.04.34-.07.67-.07 1 0 .33.03.65.07.97l-2.11 1.66c-.19.15-.25.42-.12.64l2 3.46c.12.22.39.3.61.22l2.49-1.01c.52.4 1.06.74 1.69.99l.37 2.65c.04.24.25.42.5.42h4c.25 0 .46-.18.5-.42l.37-2.65c.63-.26 1.17-.59 1.69-.99l2.49 1.01c.22.08.49 0 .61-.22l2-3.46c.12-.22.07-.49-.12-.64l-2.11-1.66Z" />
                </svg>
              </div>

              {/* Target/Goal Icon */}
              <div
                className="absolute top-2/5 left-1/8 text-zinc-600 dark:text-zinc-400"
                style={{
                  animation: "float 34s ease-in-out infinite 6s, pulse 13s ease-in-out infinite",
                }}
              >
                <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm0-14c-3.31 0-6 2.69-6 6s2.69 6 6 6 6-2.69 6-6-2.69-6-6-6zm0 10c-2.21 0-4-1.79-4-4s1.79-4 4-4 4 1.79 4 4-1.79 4-4 4zm0-6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z" />
                </svg>
              </div>

              {/* Network/Connection Icon */}
              <div
                className="absolute right-2/3 bottom-2/5 text-zinc-500 dark:text-zinc-500"
                style={{
                  animation:
                    "scaleBreath 10s ease-in-out infinite 2s, rotate 65s linear infinite reverse",
                }}
              >
                <svg className="h-8 w-8" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zM9 17H7v-7h2v7zm4 0h-2V7h2v10zm4 0h-2v-4h2v4z" />
                </svg>
              </div>
            </div>

            {/* Code & Data Visualization Elements */}
            <div className="absolute inset-0 opacity-6">
              {/* Code Brackets */}
              <div
                className="absolute top-1/4 right-1/8 font-mono text-sm text-zinc-600 dark:text-zinc-400"
                style={{
                  animation: "float 29s ease-in-out infinite 4s, rotate 90s linear infinite",
                }}
              >
                {}
              </div>

              {/* HTML Tag */}
              <div
                className="absolute bottom-1/3 left-1/8 font-mono text-xs text-zinc-500 dark:text-zinc-500"
                style={{
                  animation:
                    "scaleBreath 8s ease-in-out infinite 6s, float 41s ease-in-out infinite reverse",
                }}
              >
                &lt;div&gt;
              </div>

              {/* Percentage Symbol */}
              <div
                className="absolute top-3/5 right-1/12 text-lg font-bold text-zinc-600 dark:text-zinc-400"
                style={{
                  animation:
                    "pulse 11s ease-in-out infinite 3s, rotate 40s linear infinite reverse",
                }}
              >
                %
              </div>

              {/* Hash Symbol */}
              <div
                className="absolute right-1/3 bottom-1/6 text-base font-bold text-zinc-500 dark:text-zinc-500"
                style={{
                  animation:
                    "float 36s ease-in-out infinite 8s, scaleBreath 12s ease-in-out infinite",
                }}
              >
                #
              </div>
            </div>

            {/* More Space Effects */}
            <div className="absolute inset-0 opacity-10">
              {/* Nebula Effect */}
              <div
                className="absolute top-1/6 right-1/4 h-32 w-32 rounded-full bg-zinc-400/10 blur-xl dark:bg-zinc-600/10"
                style={{
                  animation: "pulse 20s ease-in-out infinite, float 60s ease-in-out infinite 5s",
                }}
              />

              {/* Galaxy Spiral */}
              <div
                className="absolute bottom-1/4 left-1/12 h-24 w-24 rounded-full bg-zinc-500/8 blur-lg dark:bg-zinc-500/8"
                style={{
                  animation: "rotate 80s linear infinite, scaleBreath 15s ease-in-out infinite 3s",
                }}
              />

              {/* Cosmic Dust */}
              <div
                className="absolute top-2/3 right-1/6 h-16 w-40 bg-zinc-600/5 blur-2xl dark:bg-zinc-400/5"
                style={{
                  transform: "rotate(-25deg)",
                  animation:
                    "float 45s ease-in-out infinite 7s, scaleBreath 18s ease-in-out infinite",
                }}
              />
            </div>

            {/* Career Progress Indicators */}
            <div className="absolute inset-0 opacity-7">
              {/* Progress Bar */}
              <div
                className="absolute top-1/3 right-1/12 h-1 w-12 rounded bg-zinc-400/30 dark:bg-zinc-600/30"
                style={{
                  animation:
                    "scaleBreath 9s ease-in-out infinite 2s, float 37s ease-in-out infinite",
                }}
              >
                <div className="h-full w-3/4 rounded bg-zinc-600/50 dark:bg-zinc-400/50"></div>
              </div>

              {/* Experience Level Dots */}
              <div
                className="absolute bottom-1/2 left-1/5 flex space-x-1"
                style={{
                  animation: "float 43s ease-in-out infinite 4s, pulse 14s ease-in-out infinite",
                }}
              >
                <div className="h-1 w-1 rounded-full bg-zinc-500 dark:bg-zinc-500"></div>
                <div className="h-1 w-1 rounded-full bg-zinc-500 dark:bg-zinc-500"></div>
                <div className="h-1 w-1 rounded-full bg-zinc-400 dark:bg-zinc-600"></div>
              </div>

              {/* Star Rating */}
              <div
                className="absolute top-4/5 right-2/5 text-xs text-zinc-500 dark:text-zinc-500"
                style={{
                  animation:
                    "twinkle 6s ease-in-out infinite 1s, float 39s ease-in-out infinite reverse",
                }}
              >
                ★★★★☆
              </div>
            </div>
          </div>

          <div className="relative z-10 space-y-16">
            {/* Hero Section */}
            <div className="space-y-8 text-center">
              <h1 className="font-inter-tight mb-6 text-4xl leading-tight font-bold text-zinc-900 dark:text-zinc-100">
                {t`Land Your Dream Job with`}
                <em className="relative mx-2 inline-flex items-center justify-center text-zinc-900 italic dark:text-zinc-100">
                  {t`AI-Powered`}
                  <svg
                    className="absolute inset-x-0 -z-10 fill-blue-300 dark:fill-blue-700"
                    xmlns="http://www.w3.org/2000/svg"
                    width="235"
                    height="62"
                    viewBox="0 0 223 62"
                    aria-hidden="true"
                    preserveAspectRatio="none"
                  >
                    <path d="M45.654 53.62c17.666 3.154 35.622 4.512 53.558 4.837 17.94.288 35.91-.468 53.702-2.54 8.89-1.062 17.742-2.442 26.455-4.352 8.684-1.945 17.338-4.3 25.303-7.905 3.94-1.81 7.79-3.962 10.634-6.777 1.38-1.41 2.424-2.994 2.758-4.561.358-1.563-.078-3.143-1.046-4.677-.986-1.524-2.43-2.96-4.114-4.175a37.926 37.926 0 0 0-5.422-3.32c-3.84-1.977-7.958-3.563-12.156-4.933-8.42-2.707-17.148-4.653-25.95-6.145-8.802-1.52-17.702-2.56-26.622-3.333-17.852-1.49-35.826-1.776-53.739-.978-8.953.433-17.898 1.125-26.79 2.22-8.887 1.095-17.738 2.541-26.428 4.616-4.342 1.037-8.648 2.226-12.853 3.676-4.197 1.455-8.314 3.16-12.104 5.363-1.862 1.13-3.706 2.333-5.218 3.829-1.52 1.47-2.79 3.193-3.285 5.113-.528 1.912-.127 3.965.951 5.743 1.07 1.785 2.632 3.335 4.348 4.68 2.135 1.652 3.2 2.672 2.986 3.083-.18.362-1.674.114-4.08-1.638-1.863-1.387-3.63-3.014-4.95-5.09C.94 35.316.424 34.148.171 32.89c-.275-1.253-.198-2.579.069-3.822.588-2.515 2.098-4.582 3.76-6.276 1.673-1.724 3.612-3.053 5.57-4.303 3.96-2.426 8.177-4.278 12.457-5.868 4.287-1.584 8.654-2.89 13.054-4.036 8.801-2.292 17.74-3.925 26.716-5.19C70.777 2.131 79.805 1.286 88.846.723c18.087-1.065 36.236-.974 54.325.397 9.041.717 18.07 1.714 27.042 3.225 8.972 1.485 17.895 3.444 26.649 6.253 4.37 1.426 8.697 3.083 12.878 5.243a42.11 42.11 0 0 1 6.094 3.762c1.954 1.44 3.823 3.2 5.283 5.485a12.515 12.515 0 0 1 1.63 3.88c.164.706.184 1.463.253 2.193-.063.73-.094 1.485-.247 2.195-.652 2.886-2.325 5.141-4.09 6.934-3.635 3.533-7.853 5.751-12.083 7.688-8.519 3.778-17.394 6.09-26.296 7.998-8.917 1.86-17.913 3.152-26.928 4.104-18.039 1.851-36.17 2.295-54.239 1.622-18.062-.713-36.112-2.535-53.824-6.23-5.941-1.31-5.217-2.91.361-1.852" />
                  </svg>
                </em>
                <br />
                {t`Resume Builder`}
              </h1>
              <p className="mx-auto max-w-md text-xl leading-relaxed text-zinc-600 dark:text-zinc-300">
                {t`Create professional resumes that get noticed. Our AI technology crafts compelling
                content tailored to your career goals.`}
              </p>
            </div>

            {/* Statistics Section */}
            {/* <div className="grid grid-cols-3 gap-6 text-center">
              <div className="space-y-2">
                <div className="text-3xl font-bold text-zinc-900 dark:text-zinc-100">2.5M+</div>
                <div className="text-sm font-medium text-zinc-500 dark:text-zinc-400">
                  Resumes Created
                </div>
              </div>
              <div className="space-y-2">
                <div className="text-3xl font-bold text-zinc-900 dark:text-zinc-100">87%</div>
                <div className="text-sm font-medium text-zinc-500 dark:text-zinc-400">
                  Interview Rate
                </div>
              </div>
              <div className="space-y-2">
                <div className="text-3xl font-bold text-zinc-900 dark:text-zinc-100">5.0★</div>
                <div className="text-sm font-medium text-zinc-500 dark:text-zinc-400">
                  User Rating
                </div>
              </div>
            </div> */}

            {/* Testimonial */}
            {/* <div className="rounded-xl bg-zinc-100/50 p-6 backdrop-blur-sm dark:bg-zinc-800/30">
              <div className="space-y-4 text-center">
                <div className="flex justify-center space-x-1 text-yellow-500">
                  {[...Array(5)].map((_, i) => (
                    <svg key={i} className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                    </svg>
                  ))}
                </div>
                <blockquote className="text-zinc-700 italic dark:text-zinc-300">
                  "I landed a job within 2 weeks of using CVKit. The resume analyses were spot-on!"
                </blockquote>
                <div className="text-sm text-zinc-500 dark:text-zinc-400">
                  <div className="font-medium">John Doe</div>
                  <div>Software Engineer @ Google</div>
                </div>
              </div>
            </div> */}
          </div>
        </div>
      </div>
    </div>
  );
};
